/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { SelectItem } from '@mainstage/core-components';
import FormControl from '@material-ui/core/FormControl';

import { WbSelect, WbTextField } from '@agilelab/plugin-wb-platform';
import { MenuItem } from '@material-ui/core';
import { useFormExtraConfigContext } from '../../../contexts/FormExtraConfigContext';
import { RepoUrlPickerState } from './types';

export const GithubRepoPicker = (props: {
  fieldsLabels?: {
    allowedOwnersLabel?: string;
    ownerInputLabel?: string;
    ownerInputHelper?: string;
  };
  description?: string;
  allowedOwners?: string[];
  rawErrors: string[];
  state: RepoUrlPickerState;
  onChange: (state: RepoUrlPickerState) => void;
}) => {
  const {
    allowedOwners = [],
    rawErrors,
    state,
    onChange,
    fieldsLabels,
  } = props;
  const ownerItems: SelectItem[] = allowedOwners
    ? allowedOwners.map(i => ({ label: i, value: i }))
    : [{ label: 'Loading...', value: 'loading' }];

  const { owner } = state;
  const helperText =
    fieldsLabels?.ownerInputHelper ??
    'The organization, user or project that this repo will belong to';

  const { debounceMs } = useFormExtraConfigContext();

  return (
    <>
      <FormControl
        margin="normal"
        required
        error={rawErrors?.length > 0 && !owner}
      >
        {allowedOwners?.length ? (
          <WbSelect
            value={owner}
            label={fieldsLabels?.allowedOwnersLabel ?? 'Owner Available'}
            id="ownerInput"
            onChange={e => onChange({ owner: e.target.value })}
            disabled={allowedOwners.length === 1}
            helperText={helperText}
          >
            {ownerItems.map(o => (
              <MenuItem value={o.value} key={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </WbSelect>
        ) : (
          <WbTextField
            label={fieldsLabels?.ownerInputLabel ?? 'Owner'}
            id="ownerInput"
            onChange={e => onChange({ owner: e.target.value })}
            value={owner}
            debounceMs={debounceMs}
          />
        )}
      </FormControl>
    </>
  );
};
